"use client";

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { refreshLoopVideos, useLoopVideoBox } from "@/shared/loop-video";
import { BRAND_LOGO_WHITE, Icon, type IconName } from "@/shared/ui";
import { HERO_LOOKS } from "../render/media";
import { prefersReducedMotion, useReducedMotion } from "./reduced-motion";
import { activeElapsed, setScenePaused } from "./scene-clock";

export type HeroSceneCopy = {
  stage: { label: string; pickerLabel: string; looks: readonly string[] };
  chat: {
    summary: string;
    company: string;
    channel: string;
    today: string;
    online: string;
    typing: string;
    question: string;
    answer: string;
    request: string;
    joined: string;
    consultant: string;
    consultantTyping: string;
    reply: string;
    appointment: { title: string; state: string; when: string; place: string };
    composer: string;
    /** Formatted clock times of the four messages. */
    times: { question: string; answer: string; request: string; reply: string };
  };
  backstage: { label: string } & Record<BackstageKey, { title: string; text: string }>;
};

type BackstageKey = "ai" | "handoff" | "done";

const BACKSTAGE: Record<BackstageKey, { tone: string; icon: IconName }> = {
  ai: { tone: "", icon: "bot" },
  handoff: { tone: "amber", icon: "swap" },
  done: { tone: "ok", icon: "calendar" },
};

type Line =
  | { kind: "day" }
  | { kind: "question"; text: string; time: string }
  | { kind: "answer"; text: string; time?: string }
  | { kind: "joined" }
  | { kind: "typing" }
  | { kind: "reply" };

type Item = Line & { id: string; keep?: boolean; anim?: boolean; leaving?: boolean; afterFirst?: boolean };

const Cancel = Symbol("cancel");
const AUTO_ADVANCE_MS = 9000;

function initialItems(copy: HeroSceneCopy["chat"]): Item[] {
  return [
    { id: "day", kind: "day" },
    { id: "q1", kind: "question", text: copy.question, time: copy.times.question, keep: true },
    { id: "a1", kind: "answer", text: copy.answer, time: copy.times.answer },
    { id: "q2", kind: "question", text: copy.request, time: copy.times.request, afterFirst: true },
    { id: "joined", kind: "joined", afterFirst: true },
    { id: "reply", kind: "reply", afterFirst: true },
  ];
}

/**
 * The hero scene of the prototype: the switchable consultant looks (the jar
 * first, then avatars A–C, a one-time auto-advance that stops on interaction)
 * and the scripted website-chat conversation with its "behind the scenes"
 * panel. Customer-facing replies carry no AI label (DSH-014). With reduced
 * motion, also when it is turned on while the page is open, the conversation
 * is shown complete and static and nothing auto-advances; off screen and in a
 * hidden tab the script and the auto-advance pause.
 */
export function HeroScene({ copy }: { copy: HeroSceneCopy }) {
  const scene = useRef<HTMLDivElement>(null);
  const stage = useRef<HTMLElement>(null);
  const looksBar = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLSpanElement>(null);
  const nodes = useRef(new Map<string, HTMLElement>());
  // The script's generation (a new one cancels the running script) and its pause clock.
  const run = useRef({ gen: 0, paused: true, resumedAt: 0 });
  // The look auto-advance: the looks it has advanced, the active time left
  // until the next one, and whether it has stopped for good.
  const auto = useRef({ step: 0, left: AUTO_ADVANCE_MS, stopped: false });
  const itemsRef = useRef<Item[]>([]);

  const [look, setLook] = useState(0);
  const [switching, setSwitching] = useState(false);
  const [items, setItemsState] = useState<Item[]>(() => initialItems(copy.chat));
  const [status, setStatus] = useState({ text: copy.chat.online, busy: false });
  const [composer, setComposer] = useState<{ typing: boolean; text: string }>({ typing: false, text: "" });
  const [hit, setHit] = useState(false);
  const [backstage, setBackstage] = useState<{ key: BackstageKey; swapping: boolean }>({ key: "done", swapping: false });

  // Only a dependency of the motion effects, which re-run when it changes.
  const reducedMotion = useReducedMotion();

  useLoopVideoBox(stage);

  useLayoutEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    refreshLoopVideos();
  }, [look]);

  useLayoutEffect(() => {
    const field = composerRef.current;
    if (field) field.scrollLeft = field.scrollWidth;
  }, [composer.text]);

  const selectLook = (index: number) => {
    setSwitching(true);
    setLook(index);
  };

  const stopAuto = () => {
    auto.current.stopped = true;
  };

  // Touching the picker stops the auto-advance for good, with or without reduced motion.
  useEffect(() => {
    const bar = looksBar.current;
    const events = ["pointerdown", "keydown", "focusin"] as const;
    events.forEach((type) => bar?.addEventListener(type, stopAuto));
    return () => events.forEach((type) => bar?.removeEventListener(type, stopAuto));
  }, []);

  // Looks: a gentle one-time auto-advance through every look, counted only
  // while the conversation runs. It stops for good once the visitor touches
  // the picker or every look has been shown. Reduced motion only suspends it:
  // turned off again, the advance goes on from where it stopped, as nobody
  // chose a look.
  useEffect(() => {
    const advance = auto.current;
    if (advance.stopped || prefersReducedMotion()) return;
    let last = performance.now();
    let timer = 0;
    const tick = () => {
      if (advance.stopped) return;
      const now = performance.now();
      advance.left -= activeElapsed(run.current, last, now);
      last = now;
      if (advance.left <= 0) {
        advance.step += 1;
        selectLook(advance.step % HERO_LOOKS.length);
        if (advance.step >= HERO_LOOKS.length) {
          advance.stopped = true;
          return;
        }
        advance.left = AUTO_ADVANCE_MS;
      }
      timer = window.setTimeout(tick, 250);
    };
    timer = window.setTimeout(tick, 250);
    return () => window.clearTimeout(timer);
  }, [reducedMotion]);

  // The scripted conversation: question, unlabelled answer, handoff to a named
  // consultant, a booking; then it rewinds to the first question and repeats.
  // Reduced motion keeps it from starting; turned on later, it cancels the
  // script through the cleanup.
  useEffect(() => {
    if (prefersReducedMotion()) return;
    const state = run.current;
    const chat = copy.chat;
    const timers = new Set<number>();
    const glides = new Set<Animation>();
    let counter = 0;
    const nextId = () => `m${(counter += 1)}`;
    const later = (fn: () => void, ms: number) => {
      const id = window.setTimeout(() => {
        timers.delete(id);
        fn();
      }, ms);
      timers.add(id);
    };
    const setItems = (update: (current: Item[]) => Item[]) => setItemsState(update);
    const add = (item: Item) => setItems((current) => [...current, { ...item, anim: true }]);

    const sleep = (ms: number) => {
      const generation = state.gen;
      return new Promise<void>((resolve, reject) => {
        let left = ms;
        let last = performance.now();
        const tick = () => {
          if (generation !== state.gen) return reject(Cancel);
          const now = performance.now();
          left -= activeElapsed(state, last, now);
          last = now;
          if (left <= 0) resolve();
          else later(tick, Math.min(left, 80));
        };
        later(tick, Math.min(ms, 80));
      });
    };

    const showBackstage = (key: BackstageKey) => {
      setBackstage((current) => ({ ...current, swapping: true }));
      later(() => setBackstage({ key, swapping: false }), 220);
    };

    const customerTypes = async (text: string, time: string) => {
      setComposer({ typing: true, text: "" });
      let typed = "";
      for (const character of text) {
        typed += character;
        setComposer({ typing: true, text: typed });
        await sleep(character === " " ? 22 : 24 + Math.random() * 22);
      }
      await sleep(220);
      setHit(true);
      later(() => setHit(false), 180);
      setComposer({ typing: false, text: "" });
      add({ id: nextId(), kind: "question", text, time });
    };

    const aiAnswers = async () => {
      showBackstage("ai");
      setStatus({ text: chat.typing, busy: true });
      const id = nextId();
      add({ id, kind: "answer", text: "" });
      const words = chat.answer.split(" ");
      for (let index = 0; index < words.length; index += 1) {
        const text = words.slice(0, index + 1).join(" ");
        setItems((current) => current.map((item) => (item.id === id && item.kind === "answer" ? { ...item, text } : item)));
        await sleep(70);
      }
      setItems((current) => current.map((item) => (item.id === id && item.kind === "answer" ? { ...item, time: chat.times.answer } : item)));
      setStatus({ text: chat.online, busy: false });
    };

    const consultant = async () => {
      add({ id: nextId(), kind: "joined" });
      showBackstage("handoff");
      setStatus({ text: chat.consultant, busy: true });
      await sleep(1100);
      const dots = nextId();
      add({ id: dots, kind: "typing" });
      setStatus({ text: chat.consultantTyping, busy: true });
      await sleep(900);
      setItems((current) => [...current.filter((item) => item.id !== dots), { id: nextId(), kind: "reply", anim: true }]);
      showBackstage("done");
      setStatus({ text: chat.consultant, busy: false });
    };

    // Rewind without wiping the window: everything after the first question
    // fades out, the question glides back into place and the dialogue continues.
    const rewind = async () => {
      const keep = itemsRef.current.filter((item) => item.kind === "day" || item.keep).map((item) => item.id);
      setItems((current) => current.map((item) => (keep.includes(item.id) ? item : { ...item, leaving: true })));
      await sleep(460);
      const before = keep.map((id) => nodes.current.get(id)?.getBoundingClientRect().top ?? 0);
      flushSync(() => setItems((current) => current.filter((item) => keep.includes(item.id))));
      keep.forEach((id, index) => {
        const node = nodes.current.get(id);
        if (!node) return;
        const dy = before[index] - node.getBoundingClientRect().top;
        if (!dy || !node.animate) return;
        const glide = node.animate([{ transform: `translateY(${dy}px)` }, { transform: "translateY(0)" }], { duration: 420, easing: "cubic-bezier(.2,.8,.2,1)" });
        glides.add(glide);
        glide.onfinish = () => glides.delete(glide);
      });
      setStatus({ text: chat.online, busy: false });
      await sleep(380);
    };

    const scenePass = async (first: boolean) => {
      if (first) {
        showBackstage("ai");
        await sleep(600);
      } else {
        await rewind();
        await aiAnswers();
        await sleep(400);
      }
      await customerTypes(chat.request, chat.times.request);
      await sleep(380);
      await consultant();
      await sleep(3400);
    };

    const loop = async () => {
      const generation = ++state.gen;
      let first = true;
      try {
        while (generation === state.gen) {
          await scenePass(first);
          first = false;
        }
      } catch (error) {
        if (error !== Cancel) throw error;
      }
    };

    // Start from the answered first question so the window is never empty.
    setItems((current) => current.filter((item) => !item.afterFirst));

    const hero = scene.current?.closest("section") ?? null;
    let started = false;
    const go = (visible: boolean) => {
      setScenePaused(state, !visible || document.hidden, performance.now());
      if (visible && !started) {
        started = true;
        void loop();
      }
    };
    let observer: IntersectionObserver | null = null;
    if (hero && "IntersectionObserver" in window) {
      observer = new IntersectionObserver((entries) => entries.forEach((entry) => go(entry.isIntersecting)));
      observer.observe(hero);
    } else {
      go(true);
    }
    const onVisibility = () => {
      if (document.hidden) state.paused = true;
      else go((hero?.getBoundingClientRect().bottom ?? 1) > 0);
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      state.gen += 1;
      state.paused = true;
      observer?.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
      timers.forEach((id) => window.clearTimeout(id));
      glides.forEach((glide) => glide.cancel());
      // The script stops (reduced motion turned on, new copy, unmount): the
      // complete, static conversation stays.
      setItemsState(initialItems(chat));
      setStatus({ text: chat.online, busy: false });
      setComposer({ typing: false, text: "" });
      setHit(false);
      setBackstage({ key: "done", swapping: false });
    };
  }, [copy.chat, reducedMotion]);

  const register = (id: string) => (element: HTMLElement | null) => {
    if (element) nodes.current.set(id, element);
    else nodes.current.delete(id);
  };

  const lineClass = (base: string, item: Item) => [base, item.anim ? "anim" : "", item.leaving ? "leaving" : ""].filter(Boolean).join(" ");

  const renderItem = (item: Item) => {
    const shared = { ref: register(item.id), "data-after-first": item.afterFirst ? "" : undefined } as const;
    switch (item.kind) {
      case "day":
        return <span key={item.id} className={lineClass("day", item)} {...shared}>{copy.chat.today}</span>;
      case "question":
        return <div key={item.id} className={lineClass("msg out", item)} {...shared}><p>{item.text}</p><time>{item.time}</time></div>;
      case "answer":
        return <div key={item.id} className={lineClass("msg in", item)} {...shared}><p>{item.text}</p>{item.time ? <time>{item.time}</time> : null}</div>;
      case "joined":
        return <div key={item.id} className={lineClass("sys", item)} {...shared}><Icon name="usercheck" className="ic" />{copy.chat.joined}</div>;
      case "typing":
        return <div key={item.id} className={lineClass("msg in typing", item)} {...shared}><i /><i /><i /></div>;
      case "reply":
        return (
          <div key={item.id} className={lineClass("msg in human", item)} {...shared}>
            <span className="who-tag"><Icon name="user" className="ic" />{copy.chat.consultant}</span>
            <p>{copy.chat.reply}</p>
            <div className="appt">
              <div className="pc-top"><span>{copy.chat.appointment.title}</span><span className="ok-state">{copy.chat.appointment.state}</span></div>
              <div className="ap-when">{copy.chat.appointment.when}</div>
              <div className="pc-desc">{copy.chat.appointment.place}</div>
            </div>
            <time>{copy.chat.times.reply}</time>
          </div>
        );
    }
  };

  const stageBox = BACKSTAGE[backstage.key];
  const stageCopy = copy.backstage[backstage.key];

  return (
    <div className="scene" ref={scene}>
      <div className="stage-col">
        <figure className={switching ? "jar switching" : "jar"} id="stage" ref={stage} data-look={look === 0 ? "jar" : "av"} aria-label={copy.stage.label}>
          <div className="jar-halo" aria-hidden="true" />
          <div className="av-halo" aria-hidden="true" />
          {HERO_LOOKS.map((item, index) => (
            <div key={item.id} className={`look ${index === 0 ? "look-jar" : "look-av"}${look === index ? " on" : ""}`} aria-hidden="true">
              {index === 0 ? <div className="jar-floor" /> : null}
              <video muted playsInline loop preload="none" poster={item.media.poster}>
                <source src={item.media.webm} type="video/webm" />
                <source src={item.media.mp4} type="video/mp4" />
              </video>
              {/* eslint-disable-next-line @next/next/no-img-element -- decorative SVG mark on the jar */}
              {index === 0 ? <img className="jar-logo" src={BRAND_LOGO_WHITE} alt="" /> : null}
            </div>
          ))}
        </figure>
        <div className="looks-bar" ref={looksBar}>
          <div className="looks" role="group" aria-label={copy.stage.pickerLabel}>
            {HERO_LOOKS.map((item, index) => (
              <button
                key={item.id}
                className={index === 0 ? "look-thumb jar-thumb" : "look-thumb"}
                type="button"
                aria-pressed={look === index}
                aria-label={copy.stage.looks[index]}
                onClick={() => {
                  stopAuto();
                  selectLook(index);
                }}
              >
                {/* eslint-disable-next-line @next/next/no-img-element -- the poster frame is the thumbnail */}
                <img src={item.media.poster} alt="" width={item.thumb.width} height={item.thumb.height} />
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="convo">
        <p className="sr-only">{copy.chat.summary}</p>
        <div className="chat" aria-hidden="true">
          <div className="chat-head">
            <span className="brandmark"><span className="bm-ic"><Icon name="trace" /></span></span>
            <div className="chat-who"><b>{copy.chat.company}</b><span className={status.busy ? "chat-status busy" : "chat-status"} id="chatStatus">{status.text}</span></div>
            <span className="chat-chan"><Icon name="globe" className="ic" />{copy.chat.channel}</span>
          </div>
          <div className="chat-log" id="chatLog">{items.map(renderItem)}</div>
          <div className="chat-compose">
            <span className={composer.typing ? "field typing" : "field"} id="composer" ref={composerRef}>
              {composer.typing ? <span>{composer.text}</span> : <span className="ph">{copy.chat.composer}</span>}
            </span>
            <span className={hit ? "send hit" : "send"} id="sendBtn"><Icon name="send" className="ic" /></span>
          </div>
        </div>
        <div className="backstage" aria-hidden="true">
          <span className="bs-label">{copy.backstage.label}</span>
          <div className={backstage.swapping ? "bs-body swap" : "bs-body"} id="bsBody" data-tone={stageBox.tone}>
            <span className="bs-ic"><Icon name={stageBox.icon} className="ic" /></span>
            <div><div className="bs-title" id="bsTitle">{stageCopy.title}</div><div className="bs-sub" id="bsSub">{stageCopy.text}</div></div>
          </div>
        </div>
      </div>
    </div>
  );
}
